import type { ReactNode } from "react";
import type { EpisodeSummary } from "@/api/datasetEpisode";
import { HatTextTooltip } from "@/components/tooltip";
import { formatNumber } from "@/utils";
import { formatEpisodeDuration } from "../datasetFormatters";
import { deviationSeverity, type DeviationSeverity } from "./bandDeviation";
import { deviationPosition } from "./datasetOverview";
import { diagnosticsTermLabel, diagnosticsTermTip } from "./diagnosticsTerms";
import type { ActionSourceCategory, ActionSourceModel } from "./segments/actionSourceSegments";
import { actionSourceDotColor, actionSourceTerm } from "./segments/actionSourceTerms";
import type { EpisodeDiagnosticsState } from "./useEpisodeDiagnostics";
import styles from "./EpisodeDiagnosticsSurface.module.css";

// 요약에 표시할 출처. 사람이 낸 구간만 표시한다. 다수인 INFERENCE 는 나머지이므로 따로 적지 않는다.
const SUMMARY_SOURCES: readonly ActionSourceCategory[] = ["INTERVENTION", "TELEOP"];

// 스파크라인·범례와 같은 이탈 심각도 기준(35/45%)을 쓴다. 그래서 같은 3색을 쓴다.
const SEVERITY_CLASS: Record<DeviationSeverity, string> = {
  ok: styles.severityOk,
  warn: styles.severityWarn,
  alert: styles.severityAlert,
};

/**
 * 진단 표면 맨 위의 한 줄. 보기를 바꿔도 남는 머리 숫자를 표시한다.
 *
 * 길이와 행 수는 진단이 아니라 에피소드 응답에서 온다. 그래서 진단이 미산출이거나 실패해도 남는다.
 * 기준 밖 비율은 밴드 집계 뒤 데이터셋 진단 행에서만 나온다. 값이 없으면 그 칸만 뺀다.
 * 출처는 action_source 구간이 있을 때만 표시한다. 사람이 낸 구간(INTERVENTION·TELEOP)의 합계를
 * 표시한다. INTERVENTION 이 한 번도 없으면 없다고 적는다. 리뷰어는 이 값으로 사람이
 * 낸 구간의 비중을 확인한다.
 */
export function DiagnosticsSummaryLine({
  episode,
  episodeDiagnostics,
  actionSource = null,
}: {
  episode: EpisodeSummary;
  episodeDiagnostics: EpisodeDiagnosticsState | null;
  actionSource?: ActionSourceModel | null;
}): ReactNode {
  const position =
    episodeDiagnostics?.status === "ready"
      ? deviationPosition(episodeDiagnostics.index, episode.uuid)
      : null;
  const meta = episodeMetaText(episode);

  return (
    <div className={styles.summary}>
      {meta !== "" ? <span className={styles.summaryMeta}>{meta}</span> : null}
      {position !== null ? (
        <span className={styles.summaryItem}>
          <HatTextTooltip text={diagnosticsTermTip("deviation")} placement="bottom" align="start">
            <span className={styles.summaryLabel}>{diagnosticsTermLabel("deviation")}</span>
          </HatTextTooltip>
          <span
            className={`${styles.summaryValue} ${SEVERITY_CLASS[deviationSeverity(position.value)]}`}
          >
            {Math.round(position.value * 100)}%
          </span>
          <span className={styles.summaryMeta}>
            {formatNumber(position.total)}개 중 {formatNumber(position.rank + 1)}번째
          </span>
        </span>
      ) : null}
      {actionSource === null
        ? null
        : SUMMARY_SOURCES.map((category) => (
            <SourceSummary key={category} model={actionSource} category={category} />
          ))}
    </div>
  );
}

function SourceSummary({
  model,
  category,
}: {
  model: ActionSourceModel;
  category: ActionSourceCategory;
}): ReactNode {
  const total = model.totals.find((entry) => entry.category === category) ?? null;
  // TELEOP 은 있을 때만 표시한다. 추론 데이터셋에서 "TELEOP 없음" 은 쓸모 없는 정보다.
  if (total === null && category !== "INTERVENTION") {
    return null;
  }
  const term = actionSourceTerm(category);
  return (
    <span className={styles.summaryItem}>
      <HatTextTooltip text={term.tip} placement="bottom" align="start">
        <span className={styles.summaryLabel}>
          <span
            className={styles.summaryDot}
            style={{ background: actionSourceDotColor(term.dataViz) }}
            aria-hidden="true"
          />
          {term.label}
        </span>
      </HatTextTooltip>
      {total === null ? (
        <span className={styles.summaryMeta}>없음</span>
      ) : (
        <>
          <span className={styles.summaryValue}>{formatEpisodeDuration(total.seconds)}</span>
          <span className={styles.summaryMeta}>
            {Math.round(total.share * 100)}% · {formatNumber(total.bandCount)}구간
          </span>
        </>
      )}
    </span>
  );
}

function episodeMetaText(episode: EpisodeSummary): string {
  const parts: string[] = [];
  const duration = formatEpisodeDuration(episode.duration_seconds);
  if (duration !== null) {
    parts.push(duration);
  }
  if (episode.parquet?.row_count != null) {
    parts.push(`${formatNumber(episode.parquet.row_count)}행`);
  }
  return parts.join(" · ");
}
